/**
 * THE ONE NARROW-VIEWPORT SIGNAL for the COC follow-up register.
 *
 * WHY IT EXISTS. The register is a `DataTable`, and `[data-visual='v1']
 * .ac-table` is `table-layout: fixed` (dashboard.css) with its widths
 * declared per column through COLUMN-WIDTHS-V1's `<colgroup>`. A colgroup is
 * INLINE style on a `<col>`: no media rule can reach it. So the tablet column
 * SET -- which columns exist at all -- is a render decision, and a render
 * decision needs a signal.
 *
 * WHY 900px AND NOT 834. It is not a new breakpoint. `@media (max-width:
 * 900px)` is where the shell itself already folds: TABLET-SHELL-V1 (#760,
 * founder authorisation 2026-09-09, condition B) collapses
 * `[data-visual='v1'] .dashboard-shell` to one column and turns the nav into
 * a horizontal strip there. Reading the SAME query means the rail folds and
 * the columns fold on exactly one line rather than two that drift; 834 (the
 * designed tablet width) sits inside it, which is why the tablet frames show
 * the narrow set.
 *
 * NO CSS IS ADDED BY THIS FILE. It reads a query; it declares no rule.
 *
 * SSR / TEST DEFAULT IS FALSE. `react-dom/server` has no `matchMedia`, so a
 * static render gets the DESKTOP column set -- the full-fat one. A signal
 * that guessed "narrow" off-glass would make the server render fold columns
 * nobody asked it to fold; guessing wide only ever shows MORE.
 */

import { useEffect, useState } from 'react';

/** The shell's own tablet fold (dashboard.css TABLET-SHELL-V1). */
export const NARROW_VIEWPORT_QUERY = '(max-width: 900px)';

function matchMediaOf(query: string): MediaQueryList | null {
  const w = globalThis as { matchMedia?: (q: string) => MediaQueryList };
  return typeof w.matchMedia === 'function' ? w.matchMedia(query) : null;
}

/** Read the query ONCE, honestly: false wherever matchMedia does not exist
 *  (server render, jsdom without the shim) -- never a guess. */
export function readNarrowViewport(query: string = NARROW_VIEWPORT_QUERY): boolean {
  return matchMediaOf(query)?.matches === true;
}

/**
 * TRUE while the viewport is at or below the shell's tablet fold. Subscribes
 * to the query itself rather than to `resize`, so a rotation or a split-view
 * change re-renders once, on the transition, and not on every pixel.
 */
export function useNarrowViewport(
  query: string = NARROW_VIEWPORT_QUERY,
): boolean {
  const [narrow, setNarrow] = useState<boolean>(() => readNarrowViewport(query));
  useEffect(() => {
    const mql = matchMediaOf(query);
    if (mql === null) return;
    // Re-read on mount: the first paint may have used the SSR default.
    setNarrow(mql.matches);
    const onChange = (e: MediaQueryListEvent) => setNarrow(e.matches);
    // `addEventListener` is the current API; older WebKit ships only
    // `addListener`, which the deployed QA Safari/iPad still uses.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    const legacy = mql as unknown as {
      addListener?: (cb: (e: MediaQueryListEvent) => void) => void;
      removeListener?: (cb: (e: MediaQueryListEvent) => void) => void;
    };
    legacy.addListener?.(onChange);
    return () => legacy.removeListener?.(onChange);
  }, [query]);
  return narrow;
}
